import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Developers from "../components/Developers";
import {
  ArrowPathIcon,
  BanknotesIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  SignalIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const benefits = [
  {
    title: "Works without internet",
    description: "Reach customers on feature phones, smartphones, and low-connectivity networks.",
    icon: SignalIcon,
  },
  {
    title: "Real-time sessions",
    description: "Guide users through fast menus for payments, support, bookings, and account checks.",
    icon: ArrowPathIcon,
  },
  {
    title: "Built for scale",
    description: "Launch reliable short-code experiences that can handle high-volume interactions.",
    icon: UserGroupIcon,
  },
  {
    title: "Secure by design",
    description: "Support sensitive flows such as PIN prompts, identity checks, and transaction confirmations.",
    icon: ShieldCheckIcon,
  },
];

const useCases = [
  ["Mobile banking", "Balances, transfers, mini statements, and bill payments."],
  ["Customer support", "Self-service menus, ticket creation, and feedback collection."],
  ["Subscriptions", "Plan sign-ups, renewals, opt-ins, and service management."],
  ["Information services", "On-demand public notices, prices, news, and account updates."],
];

const menuFlow = [
  "Welcome to Whistle Bank",
  "1. Check balance",
  "2. Send money",
  "3. Pay bill",
];

function USSDMessaging() {
  return (
    <div className="bg-slate-50">
      <Navbar />

      <section className="relative overflow-hidden bg-[#071d33] text-white">
        <div className="absolute inset-x-0 bottom-0 h-28 bg-slate-50" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[85rem] gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_0.86fr] lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">USSD messaging</p>
            <h1 className="mt-5 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Simple mobile services for every customer.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50">
              Build fast text-based journeys that work on any phone. Whistle Pulse helps teams launch USSD menus for
              banking, support, subscriptions, surveys, and account access without requiring mobile data.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="https://dashboard.whistlepulse.com/auth/register"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-950/30 transition hover:bg-blue-400"
              >
                Start building
              </a>
              <a
                href="/contact-us"
                className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Talk to sales
              </a>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-sm">
            <div className="rounded-[2rem] border border-white/10 bg-white/10 p-3 shadow-2xl shadow-black/30 backdrop-blur">
              <div className="rounded-[1.6rem] bg-[#041629] p-5">
                <div className="mx-auto mb-5 h-1.5 w-20 rounded-full bg-white/20" />
                <div className="rounded-xl bg-[#071d33] p-5 font-mono text-sm text-blue-50">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <span>*920#</span>
                    <span className="rounded bg-emerald-400/15 px-2 py-1 text-xs text-emerald-200">live</span>
                  </div>
                  <div className="mt-5 space-y-3">
                    {menuFlow.map((item) => (
                      <p key={item}>{item}</p>
                    ))}
                  </div>
                  <div className="mt-6 rounded-lg border border-blue-300/20 bg-blue-300/10 p-3">
                    <p className="text-blue-100">Reply with option:</p>
                    <p className="mt-2 text-2xl font-bold text-white">2</p>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-2">
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((key) => (
                    <span key={key} className="rounded-lg bg-white/10 py-3 text-center text-sm text-white">
                      {key}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <benefit.icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h2 className="mt-5 text-lg font-bold text-slate-950">{benefit.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-[85rem] gap-10 px-4 pb-16 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Common workflows</p>
          <h2 className="mt-4 text-3xl font-bold text-slate-950 sm:text-4xl">Launch service menus customers can use instantly.</h2>
          <p className="mt-5 text-base leading-7 text-slate-600">
            USSD is ideal for high-frequency tasks where speed and access matter more than screens, downloads, or data.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {useCases.map(([title, description]) => (
            <div key={title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-blue-600" aria-hidden="true" />
                <div>
                  <h3 className="font-semibold text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-[85rem] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-3 lg:px-8">
          {[
            [DevicePhoneMobileIcon, "Design the menu", "Map prompts, options, validation rules, and final responses."],
            [ChatBubbleBottomCenterTextIcon, "Connect your service", "Route sessions to your business logic, support desk, or transaction system."],
            [BanknotesIcon, "Go live on short code", "Deploy, monitor usage, and improve flows from customer behavior."],
          ].map(([Icon, title, description], index) => (
            <div key={title} className="relative rounded-lg border border-slate-200 p-6">
              <span className="absolute right-5 top-5 font-mono text-sm font-bold text-slate-200">0{index + 1}</span>
              <Icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h3 className="mt-5 text-xl font-bold text-slate-950">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
            </div>
          ))}
        </div>
      </section>

      <Developers />
      <Footer />
    </div>
  );
}

export default USSDMessaging;
